import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { resolveApiErrorMessage } from '../services/authApi';

export default function LoginPage() {
  const rmc2Url = 'https://findesk.rmc2.com.ph/';
  const hunterLabsUrl = 'https://hunterlabs.solutions/';
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [form, setForm] = useState({
    login: '',
    password: '',
    rememberMe: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isDarkTheme = theme === 'dark';
  const rmcLogoPath = useMemo(
    () => (isDarkTheme ? '/logo512-white.png' : '/logo512.png'),
    [isDarkTheme]
  );
  const hunterLabsLogoPath = useMemo(
    () => (isDarkTheme ? '/logo192-hlabs-white.png' : '/logo192-hlabs-black.png'),
    [isDarkTheme]
  );

  function handleChange(event) {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await login(form);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setErrorMessage(
        resolveApiErrorMessage(error, 'Login failed. Please check your credentials.')
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={`auth-page ${isDarkTheme ? 'theme-dark' : 'theme-light'}`}>
      <div className="auth-shell">
        <aside className="auth-side">
          <a
            className="auth-brand-row brand-link"
            href={rmc2Url}
            target="_blank"
            rel="noreferrer"
          >
            <img src={rmcLogoPath} alt="RMC2" className="rmc-logo" />
            <div>
              <h1>RMC2 Findesk</h1>
              <p>Collections and field operations portal</p>
            </div>
          </a>

          <div className="auth-side-intro">
            <h3>Welcome back</h3>
            <p>Sign in to continue to your assigned modules and dashboard insights.</p>
          </div>

          <ul className="auth-nav-preview">
            <li className="active">Dashboard</li>
            <li>Agents</li>
            <li>Collections</li>
            <li>Commissions</li>
            <li>Reports</li>
          </ul>

          <div className="auth-side-footer">
            <span>Powered by</span>
            <a
              className="powered-by-link"
              href={hunterLabsUrl}
              target="_blank"
              rel="noreferrer"
            >
              <img src={hunterLabsLogoPath} alt="HunterLabs" className="hunterlabs-logo" />
              <span>HunterLabs</span>
            </a>
          </div>
        </aside>

        <section className="auth-main">
          <div className="auth-toolbar">
            <button type="button" className="theme-toggle" onClick={toggleTheme}>
              {isDarkTheme ? 'Light Theme' : 'Dark Theme'}
            </button>
          </div>

          <form className="auth-form-card login-form" onSubmit={handleSubmit}>
            <h2>Sign in</h2>
            <p className="auth-subtitle">Use your username or email to access FinDesk.</p>

            {location.state?.successMessage ? (
              <div className="success-box">{location.state.successMessage}</div>
            ) : null}

            {errorMessage ? <div className="error-box">{errorMessage}</div> : null}

            <label htmlFor="login">Username or Email</label>
            <input
              id="login"
              name="login"
              type="text"
              value={form.login}
              onChange={handleChange}
              autoComplete="username"
              required
            />

            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              autoComplete="current-password"
              required
            />

            <label className="remember-row" htmlFor="rememberMe">
              <input
                id="rememberMe"
                name="rememberMe"
                type="checkbox"
                checked={form.rememberMe}
                onChange={handleChange}
              />
              Keep this session signed in
            </label>

            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in...' : 'Login'}
            </button>

            {/* <p className="form-switch-text">
              No account yet? <Link to="/register">Create one here</Link>
            </p> */}
          </form>
        </section>
      </div>
    </div>
  );
}
